import { useEffect, useRef } from 'react';

/**
 * Ask before the page unloads — a reload, closing the tab, leaving the site — while `shouldWarn()` says work
 * would be lost (an edit not saved yet, a save still on its way). The browser shows its own "Leave site?"
 * dialog; a page can only ask for it, not word it.
 *
 * `shouldWarn` is read at the moment of leaving (through a ref), so it can check refs — a pending timer, an
 * in-flight request — and not only state. In-app route changes aren't guarded here: they don't abort a save
 * that's already been sent.
 */
export function useBeforeUnloadGuard(shouldWarn: () => boolean) {
  const shouldWarnRef = useRef(shouldWarn);
  useEffect(() => {
    shouldWarnRef.current = shouldWarn;
  });
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!shouldWarnRef.current()) return;
      event.preventDefault();
      // Older browsers ask only when `returnValue` is set too.
      event.returnValue = true;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);
}
